import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApi, useRouteRefParams } from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import {
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import {
  AccountEntity,
  accountDotColor,
  RULE_SEVERITIES,
  RuleEntity,
  RuleSetEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  affectedCount,
  maxSavingOf,
  pillClass,
  rulesetsOf,
  ruleTitle,
  savingText,
  scanLabel,
  severityOf,
} from '../EntityPages/shared';
import { ProviderChip } from '../shared/ProviderChip';
import {
  PageSkeleton,
  useSelectPopover,
} from '@internal/backstage-plugin-devops-react';
import styles from '../compliance-page.module.css';
import pStyles from './project-compliance.module.css';

const CATEGORIES = ['Security', 'Finance', 'Housekeeping'];

const RING_CIRCUMFERENCE = 257.6;

type CategoryFilter = 'all' | (typeof CATEGORIES)[number];
type StatusFilter = 'Failed' | 'Passed' | 'Unknown' | 'All';

const accentOf = (category: string): string => {
  if (category === 'Security') {
    return styles.accentSecurity;
  }
  if (category === 'Finance') {
    return styles.accentFinance;
  }
  return styles.accentHousekeeping;
};

/**
 * Project (Domain) Compliance tab, 1:1 with
 * design/project-compliance-page-mockup.html. Rules of all the domain's
 * accounts form (rule × account) pairs — every row is one rule evaluated
 * in one account (the same rule can fail in account A and pass in B).
 * Row click drills into that account's Compliance tab with the current
 * filters (?cat=&st=). Deep links ?cat=&st= are read on load.
 */
export function ProjectComplianceTab() {
  const { name } = useRouteRefParams(entityRouteRef);
  const { search } = useLocation();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);

  const [initialCategory, initialStatus] = useMemo(() => {
    const params = new URLSearchParams(search);
    const cat = params.get('cat');
    const st = params.get('st');
    return [
      CATEGORIES.includes(cat as CategoryFilter)
        ? (cat as CategoryFilter)
        : 'all',
      ['Failed', 'Passed', 'Unknown', 'All'].includes(st ?? '')
        ? (st as StatusFilter)
        : 'Failed',
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountSelect = useSelectPopover();
  const severitySelect = useSelectPopover();
  const [accounts, setAccounts] = useState<AccountEntity[]>([]);
  const [rules, setRules] = useState<RuleEntity[]>([]);
  const [ruleSets, setRuleSets] = useState<RuleSetEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [category, setCategory] = useState<CategoryFilter>(initialCategory);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [selectedSeverities, setSelectedSeverities] = useState<Set<string>>(
    new Set(),
  );
  const [selectedAccountNames, setSelectedAccountNames] = useState<Set<string>>(
    new Set(),
  );
  const [selectedFrameworks, setSelectedFrameworks] = useState<Set<string>>(
    new Set(),
  );
  const [query, setQuery] = useState('');
  const [groupByAccount, setGroupByAccount] = useState(true);

  const accountNames = useMemo(
    () => accounts.map(account => account.metadata.name),
    [accounts],
  );

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Account', 'spec.domain': name } })
      .then(({ items }) => {
        if (!cancelled) {
          setAccounts(items as AccountEntity[]);
        }
      })
      .catch(() => {
        // The tab stays usable with empty aggregates.
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, name]);

  useEffect(() => {
    if (accountNames.length === 0) {
      return () => {};
    }
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: accountNames.map(accountName => ({
          kind: 'Rule',
          'spec.account': accountName,
        })),
      })
      .then(({ items }) => {
        if (!cancelled) {
          setRules(items as RuleEntity[]);
        }
      })
      .catch(() => {
        // The table degrades to empty.
      });
    catalogApi
      .getEntities({
        filter: accountNames.map(accountName => ({
          kind: 'RuleSet',
          'spec.account': accountName,
        })),
      })
      .then(({ items }) => {
        if (!cancelled) {
          setRuleSets(items as RuleSetEntity[]);
        }
      })
      .catch(() => {
        // The frameworks strip degrades to names only.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, accountNames]);

  const accountBy = useMemo(() => {
    const map = new Map<string, AccountEntity>();
    accounts.forEach(account => map.set(account.metadata.name, account));
    return map;
  }, [accounts]);

  const failedByAccount = useMemo(() => {
    const map = new Map<string, number>();
    rules.forEach(rule => {
      if (rule.spec.result === 'Failed') {
        map.set(
          rule.spec.account,
          (map.get(rule.spec.account) ?? 0) + 1,
        );
      }
    });
    return map;
  }, [rules]);

  const frameworks = useMemo(() => {
    const counters = new Map<
      string,
      { title: string; failed: number; total: number }
    >();
    rules.forEach(rule => {
      rulesetsOf(rule).forEach(rulesetName => {
        const entry =
          counters.get(rulesetName) ??
          { title: rulesetName, failed: 0, total: 0 };
        entry.total += 1;
        if (rule.spec.result === 'Failed') {
          entry.failed += 1;
        }
        counters.set(rulesetName, entry);
      });
    });
    return [...counters.entries()]
      .map(([rulesetName, entry]) => {
        const ruleSet = ruleSets.find(
          candidate => candidate.metadata.name === rulesetName,
        );
        const rawType = (
          ruleSet?.spec.rulesetType ??
          ruleSet?.spec.type ??
          'benchmark'
        ).toLowerCase();
        return {
          name: rulesetName,
          title: ruleSet?.metadata.title ?? entry.title,
          type: rawType === 'controls' ? 'controls' : 'benchmark',
          rulesTotal: entry.total,
          failed: entry.failed,
        };
      })
      .sort((a, b) => b.failed - a.failed);
  }, [rules, ruleSets]);

  const filteredRules = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rules.filter(rule => {
      if (category !== 'all' && rule.spec.category !== category) {
        return false;
      }
      if (status !== 'All' && (rule.spec.result ?? 'Unknown') !== status) {
        return false;
      }
      if (
        selectedSeverities.size > 0 &&
        !selectedSeverities.has(severityOf(rule))
      ) {
        return false;
      }
      if (
        selectedAccountNames.size > 0 &&
        !selectedAccountNames.has(rule.spec.account)
      ) {
        return false;
      }
      if (
        selectedFrameworks.size > 0 &&
        !rulesetsOf(rule).some(rulesetName =>
          selectedFrameworks.has(rulesetName),
        )
      ) {
        return false;
      }
      if (q) {
        const haystack = [
          ruleTitle(rule),
          rule.spec.type ?? '',
          rule.metadata.description ?? '',
          rule.spec.account,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [
    rules,
    category,
    status,
    selectedSeverities,
    selectedAccountNames,
    selectedFrameworks,
    query,
  ]);

  const stats = useMemo(() => {
    const failed = rules.filter(rule => rule.spec.result === 'Failed');
    const scored = rules.filter(rule => rule.spec.result !== 'Unknown');
    const passed = scored.filter(rule => rule.spec.result === 'Passed').length;
    const affected = new Set(
      failed.flatMap(rule =>
        (rule.spec.resources ?? []).map(resource => resource.id),
      ),
    );
    const saving = failed.reduce((sum, rule) => sum + (maxSavingOf(rule) ?? 0), 0);
    return {
      score: scored.length ? Math.round((passed / scored.length) * 100) : 0,
      failedCount: failed.length,
      rulesTotal: rules.length,
      unknownCount: rules.length - scored.length,
      affected: affected.size,
      accountsInvolved: new Set(failed.map(rule => rule.spec.account)).size,
      saving,
    };
  }, [rules]);

  const failedByCategory = (cat: string) =>
    rules.filter(
      rule => rule.spec.category === cat && rule.spec.result === 'Failed',
    ).length;

  const toggleSeverity = (severity: string) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev);
      if (next.has(severity)) {
        next.delete(severity);
      } else {
        next.add(severity);
      }
      return next;
    });
  };

  const toggleAccount = (accountName: string) => {
    setSelectedAccountNames(prev => {
      const next = new Set(prev);
      if (next.has(accountName)) {
        next.delete(accountName);
      } else {
        next.add(accountName);
      }
      return next;
    });
  };

  const toggleFramework = (rulesetName: string) => {
    setSelectedFrameworks(prev => {
      const next = new Set(prev);
      if (next.has(rulesetName)) {
        next.delete(rulesetName);
      } else {
        next.add(rulesetName);
      }
      return next;
    });
  };

  const resetFilters = () => {
    setCategory('all');
    setStatus('Failed');
    setSelectedSeverities(new Set());
    setSelectedAccountNames(new Set());
    setSelectedFrameworks(new Set());
    setQuery('');
  };

  const accountComplianceHref = (account: string) =>
    `/catalog/default/account/${account}/compliance?cat=${category === 'all' ? '' : category}&st=${status}`;

  const ruleRow = (rule: RuleEntity) => {
    const account = accountBy.get(rule.spec.account);
    const provider = (account?.spec.type ?? '').toUpperCase();
    const saving = savingText(rule);
    const dotColor = accountDotColor(
      Math.max(0, accountNames.indexOf(rule.spec.account)),
    );
    return (
      <tr
        key={rule.metadata.name}
        className={styles.row}
        onClick={() => navigate(accountComplianceHref(rule.spec.account))}
      >
        <td>
          <span className={styles.ruleName}>{ruleTitle(rule)}</span>
          <div className={styles.ruleCode}>{rule.spec.type}</div>
          <div className={styles.ruleDesc} title={rule.metadata.description}>
            {rule.metadata.description ?? ''}
          </div>
        </td>
        <td>
          <Link
            className={pStyles.accCell}
            to={accountComplianceHref(rule.spec.account)}
            onClick={event => event.stopPropagation()}
          >
            <span
              className={pStyles.accDot}
              style={{ background: dotColor }}
            />
            <ProviderChip provider={provider} />
            {account?.metadata.title ?? rule.spec.account}
          </Link>
        </td>
        <td>
          <span className={`${styles.pill} ${pillClass(styles, severityOf(rule))}`}>
            <span className={styles.pillDot} />
            {severityOf(rule)}
          </span>
        </td>
        <td>
          <span
            className={`${styles.pill} ${pillClass(styles, rule.spec.result ?? 'Unknown')}`}
          >
            <span className={styles.pillDot} />
            {rule.spec.result}
          </span>
        </td>
        <td className={styles.num}>{affectedCount(rule) || '—'}</td>
        <td>
          {saving ? (
            <span className={styles.saving}>{saving}</span>
          ) : (
            <span className={styles.muted}>—</span>
          )}
        </td>
        <td>
          {rulesetsOf(rule).slice(0, 2).map(rulesetName => {
            const framework = frameworks.find(fw => fw.name === rulesetName);
            return (
              <span
                key={rulesetName}
                className={`${styles.fwTag} ${framework?.type === 'controls' ? styles.fwTagControl : styles.fwTagBench}`}
                title={framework?.title ?? rulesetName}
              >
                {framework?.title ?? rulesetName}
              </span>
            );
          })}
        </td>
        <td className={styles.muted} style={{ whiteSpace: 'nowrap' }}>
          {scanLabel(rule.spec.reportCreationTime)}
        </td>
      </tr>
    );
  };

  const groupedSections = useMemo(() => {
    if (!groupByAccount) {
      return null;
    }
    const groups = new Map<string, RuleEntity[]>();
    filteredRules.forEach(rule => {
      const groupRows = groups.get(rule.spec.account) ?? [];
      groupRows.push(rule);
      groups.set(rule.spec.account, groupRows);
    });
    return [...groups.entries()].sort(
      ([, a], [, b]) => b.length - a.length,
    );
  }, [filteredRules, groupByAccount]);

  const chips: { key: string; label: string; tip: string; onRemove: () => void }[] =
    [];
  if (category !== 'all') {
    chips.push({ key: 'cat', label: category, tip: category, onRemove: () => setCategory('all') });
  }
  if (status !== 'Failed') {
    chips.push({ key: 'st', label: status, tip: `Status: ${status}`, onRemove: () => setStatus('Failed') });
  }
  selectedAccountNames.forEach(accountName => {
    chips.push({
      key: `acc:${accountName}`,
      label: accountBy.get(accountName)?.metadata.title ?? accountName,
      tip: 'Account',
      onRemove: () => toggleAccount(accountName),
    });
  });
  selectedSeverities.forEach(severity => {
    chips.push({
      key: `sev:${severity}`,
      label: severity,
      tip: `Severity: ${severity}`,
      onRemove: () => toggleSeverity(severity),
    });
  });
  selectedFrameworks.forEach(rulesetName => {
    const framework = frameworks.find(fw => fw.name === rulesetName);
    chips.push({
      key: `fw:${rulesetName}`,
      label: framework?.title ?? rulesetName,
      tip: 'Framework',
      onRemove: () => toggleFramework(rulesetName),
    });
  });
  if (query) {
    chips.push({ key: 'q', label: `“${query}”`, tip: 'Search', onRemove: () => setQuery('') });
  }

  const ringClass = () => {
    if (stats.score >= 80) {
      return styles.ringSuccess;
    }
    if (stats.score >= 50) {
      return styles.ringWarning;
    }
    return styles.ringDanger;
  };

  const exportCsv = () => {
    const header = 'Rule,Code,Account,Category,Severity,Status,Affected,Max saving,Frameworks,Last scan';
    const lines = filteredRules.map(rule =>
      [
        ruleTitle(rule),
        rule.spec.type ?? '',
        rule.spec.account ?? '',
        rule.spec.category ?? '',
        severityOf(rule),
        rule.spec.result ?? '',
        affectedCount(rule),
        maxSavingOf(rule) ?? 0,
        rulesetsOf(rule).join(' | '),
        rule.spec.reportCreationTime ?? '',
      ]
        .map(cell => `"${String(cell).replace(/"/g, '""')}"`)
        .join(','),
    );
    const blob = new Blob([[header, ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `compliance-${name}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <PageSkeleton rows={10} />;
  }

  return (
    <>
      {/* summary band: score + category cards */}
      <div className={styles.summary}>
        <div className={`${styles.card} ${styles.scoreCard}`}>
          <div className={styles.ring}>
            <svg width="96" height="96" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r="41"
                fill="none"
                stroke="var(--bui-bg-neutral-2)"
                strokeWidth="9"
              />
              <circle
                cx="48"
                cy="48"
                r="41"
                fill="none"
                className={ringClass()}
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - stats.score / 100)}
              />
            </svg>
            <div className={styles.ringVal}>
              <div>
                <b>{stats.score}</b>
                <span>score</span>
              </div>
            </div>
          </div>
          <div className={styles.scoreMeta}>
            <h3>Compliance score</h3>
            <p>
              Aggregated across {accounts.length} accounts; <b>Unknown</b>{' '}
              rules are excluded.
            </p>
          </div>
        </div>

        <div className={styles.catGrid}>
          {CATEGORIES.map(cat => {
            const rows = rules.filter(rule => rule.spec.category === cat);
            const passed = rows.filter(rule => rule.spec.result === 'Passed')
              .length;
            const failed = rows.length - passed;
            const pct = rows.length ? Math.round((passed / rows.length) * 100) : 0;
            return (
              <button
                key={cat}
                type="button"
                className={`${styles.card} ${styles.catCard} ${category === cat ? styles.catCardSelected : ''}`}
                onClick={() => setCategory(category === cat ? 'all' : (cat as CategoryFilter))}
              >
                <span className={styles.catHead}>
                  <span className={styles.catName}>
                    <span className={`${styles.catNameDot} ${accentOf(cat)}`} />
                    {cat}
                  </span>
                  <span className={styles.catCounts}>
                    <b>{failed}</b>
                    <span>failed of {rows.length}</span>
                  </span>
                </span>
                <span className={styles.bar}>
                  <i
                    className={accentOf(cat)}
                    style={{ width: `${pct}%` }}
                  />
                </span>
                <span className={styles.catFoot}>
                  <span>
                    <b>{passed}</b> passed
                  </span>
                  <span>
                    {rows.length ? `${pct}%` : '—'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* stat strip */}
      <div className={styles.statStrip}>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Failed rules</span>
          <span className={`${styles.v} ${styles.num}`}>
            {stats.failedCount}
            <small>
              of {stats.rulesTotal}
              {stats.unknownCount > 0 ? ` · ${stats.unknownCount} unknown` : ''}
            </small>
          </span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Affected resources</span>
          <span className={`${styles.v} ${styles.num}`}>{stats.affected}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Accounts involved</span>
          <span className={`${styles.v} ${styles.num}`}>
            {stats.accountsInvolved}
            <small>of {accounts.length}</small>
          </span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Est. monthly saving</span>
          <span className={`${styles.v} ${styles.num}`}>
            {stats.saving
              ? `$${Math.round(stats.saving).toLocaleString('en-US')}`
              : '—'}
          </span>
        </div>
      </div>

      {/* frameworks strip: aggregated over the accounts */}
      <div className={styles.fwBand}>
        <div className={styles.fwScroll}>
          {frameworks.slice(0, 6).map(fw => {
            const pct = fw.rulesTotal
              ? Math.round(((fw.rulesTotal - fw.failed) / fw.rulesTotal) * 100)
              : null;
            return (
              <button
                key={fw.name}
                type="button"
                className={`${styles.card} ${styles.fwCard} ${selectedFrameworks.has(fw.name) ? styles.fwCardSelected : ''}`}
                onClick={() => toggleFramework(fw.name)}
                title={fw.title}
              >
                <span className={styles.fwHead}>
                  <span
                    className={`${styles.fwType} ${fw.type === 'controls' ? styles.fwTypeControls : styles.fwTypeBenchmark}`}
                  >
                    {fw.type}
                  </span>
                  <span className={styles.fwMeta}>{fw.rulesTotal} rules</span>
                </span>
                <span className={styles.fwName}>{fw.title}</span>
                <span className={styles.fwStats}>
                  <span className={styles.fwPct}>{pct === null ? '—' : `${pct}%`}</span>
                  <span className={styles.fwMeta}>passed · {fw.failed} failed</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* toolbar */}
      <div className={styles.toolbar} role="toolbar" aria-label="Compliance filters">
        <ToggleButtonGroup
          aria-label="Category"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([category])}
          onSelectionChange={keys => {
            const next = [...keys][0] as CategoryFilter | undefined;
            setCategory(next ?? 'all');
          }}
        >
          <ToggleButton key="all" id="all" size="small">
            All
          </ToggleButton>
          {CATEGORIES.map(cat => (
            <ToggleButton key={cat} id={cat} size="small">
              {cat}{' '}
              <span className={styles.segCount}>{failedByCategory(cat)}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <label className={styles.search}>
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search rules, accounts, codes…"
            aria-label="Search rules"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </label>

        <button
          type="button"
          ref={accountSelect.triggerRef}
          className={styles.selectBtn}
          onClick={accountSelect.toggle}
          aria-expanded={accountSelect.isOpen}
          aria-haspopup="true"
        >
          Account
          {selectedAccountNames.size > 0 && (
            <span className={styles.selectBtnCount}>
              {selectedAccountNames.size}
            </span>
          )}{' '}
          <span className={styles.selectBtnArrow}>▼</span>
        </button>
        <Popover {...accountSelect.popoverProps} placement="bottom start" hideArrow>
            <div className={styles.sevPopover}>
              {accounts.map(account => (
                <label key={account.metadata.name} className={pStyles.accRow}>
                  <input
                    type="checkbox"
                    checked={selectedAccountNames.has(account.metadata.name)}
                    onChange={() => toggleAccount(account.metadata.name)}
                  />
                  <ProviderChip provider={account.spec.type} />
                  <span className={pStyles.nm}>
                    {account.metadata.title ?? account.metadata.name}
                  </span>
                  <span className={pStyles.fail}>
                    {failedByAccount.get(account.metadata.name) ?? 0} failed
                  </span>
                </label>
              ))}
              {accounts.length === 0 && (
                <span className={styles.muted}>No accounts</span>
              )}
            </div>
        </Popover>

        <button
          type="button"
          ref={severitySelect.triggerRef}
          className={styles.selectBtn}
          onClick={severitySelect.toggle}
          aria-expanded={severitySelect.isOpen}
          aria-haspopup="true"
        >
          Severity
          {selectedSeverities.size > 0 && (
            <span className={styles.selectBtnCount}>
              {selectedSeverities.size}
            </span>
          )}{' '}
          <span className={styles.selectBtnArrow}>▼</span>
        </button>
        <Popover {...severitySelect.popoverProps} placement="bottom start" hideArrow>
            <div className={styles.sevPopover}>
              {(RULE_SEVERITIES as readonly string[]).map(severity => (
                <label key={severity}>
                  <input
                    type="checkbox"
                    checked={selectedSeverities.has(severity)}
                    onChange={() => toggleSeverity(severity)}
                  />
                  <span className={`${styles.pill} ${pillClass(styles, severity)}`}>
                    <span className={styles.pillDot} />
                    {severity}
                  </span>
                </label>
              ))}
            </div>
        </Popover>

        <ToggleButtonGroup
          aria-label="Status"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([status])}
          onSelectionChange={keys => {
            const next = [...keys][0] as StatusFilter | undefined;
            setStatus(next ?? 'Failed');
          }}
        >
          {(['Failed', 'Passed', 'Unknown', 'All'] as StatusFilter[]).map(
            value => (
              <ToggleButton key={value} id={value} size="small">
                {value}
              </ToggleButton>
            ),
          )}
        </ToggleButtonGroup>

        <button
          type="button"
          className={`${pStyles.groupToggleBtn} ${groupByAccount ? pStyles.on : ''}`}
          onClick={() => setGroupByAccount(current => !current)}
          aria-pressed={groupByAccount}
          title="Group rows by account"
        >
          ⇅ Account
        </button>

        <button
          type="button"
          className={styles.exportBtn}
          style={{ marginLeft: 'auto' }}
          onClick={exportCsv}
          title="Export filtered rules to CSV"
        >
          ↓ Export
        </button>
      </div>

      {/* removable filter chips */}
      <div className={styles.chips}>
        {chips.map(chip => (
          <span key={chip.key} className={styles.chip} title={chip.tip}>
            <b>{chip.label}</b>
            <button
              type="button"
              className={styles.chipButton}
              onClick={chip.onRemove}
              aria-label="Remove filter"
            >
              ✕
            </button>
          </span>
        ))}
        {chips.length > 0 && (
          <button type="button" className={styles.chipClear} onClick={resetFilters}>
            Clear all
          </button>
        )}
        <span className={styles.resultNote}>
          {filteredRules.length} of {rules.length} rules shown
        </span>
      </div>

      {/* rules table */}
      <div className={`${styles.card} ${styles.tableCard}`}>
        <table className={styles.table} aria-label="Compliance rules">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Account</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Affected</th>
              <th>Est. saving</th>
              <th>Frameworks</th>
              <th>Last scan</th>
            </tr>
          </thead>
          <tbody>
            {groupedSections
              ? groupedSections.map(([accountName, groupRules]) => {
                  const failed = groupRules.filter(
                    rule => rule.spec.result === 'Failed',
                  ).length;
                  const account = accountBy.get(accountName);
                  const provider = (account?.spec.type ?? '').toUpperCase();
                  const dotColor = accountDotColor(
                    Math.max(0, accountNames.indexOf(accountName)),
                  );
                  return (
                    <Fragment key={`group-${accountName}`}>
                      <tr className={styles.groupRow}>
                        <td colSpan={8}>
                          <span className={pStyles.accCell}>
                            <span
                              className={pStyles.accDot}
                              style={{ background: dotColor }}
                            />
                            <ProviderChip provider={provider} />
                            {account?.metadata.title ?? accountName}
                          </span>
                          <span className={styles.groupCount}>
                            {groupRules.length} rules · {failed} failed
                          </span>
                        </td>
                      </tr>
                      {groupRules.map(ruleRow)}
                    </Fragment>
                  );
                })
              : filteredRules.map(ruleRow)}
            {filteredRules.length === 0 && (
              <tr>
                <td colSpan={8} className={styles.muted}>
                  No rules match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
